<template>
  <div class="exercise-modal flat-scroll">
    <div class="exercise-modal__modal">
      <div class="exercise-modal__wrapper">
        <div class="exercise-modal__content-wrapper">
          <TitleBar
            :exercise="exercise"
            :canSendIssues="canSendIssues"
            @closeModal="$emit('closeModal')"
            @startIssue="startIssue"
          >
            <template class="exercise-modal__header-nav" v-slot:nav>
              <ArrowLeft
                class="exercise-modal__header-arrow"
                @click="$emit('previousExercise')"
              />
              <span class="exercise-modal__header-nav__numbers"
                >{{ currentExerciseIndex }}/{{ exercisesList.length }}</span
              >
              <ArrowLeft
                class="exercise-modal__header-arrow exercise-modal__header-arrow--rotate"
                @click="$emit('nextExercise')"
              />
            </template>
          </TitleBar>
          <div class="exercise-modal__body">
            <div class="exercise-modal__left-navbar">
              <div
                @click="setTab(tabs.exercise)"
                :class="
                  `button ${
                    activeTab === tabs.exercise ? 'active' : ''
                  } exercise-tab`
                "
              >
                <ExerciseIcon class="icon" />
              </div>
              <div
                @click="setTab(tabs.files)"
                :class="
                  `button files-tab ${activeTab === tabs.files ? 'active' : ''}`
                "
              >
                <div class="file-icon">
                  <FilesIcon class="icon" />
                  <div
                    v-if="exercise.files && exercise.files.length"
                    class="files-number"
                    :title="
                      `This exercise has ${exercise.files.length} file associated`
                    "
                  >
                    {{ exercise.files.length }}
                  </div>
                </div>
              </div>
              <div
                @click="setTab(tabs.attempts)"
                :class="
                  `button attempts-tab ${
                    activeTab === tabs.attempts ? 'active' : ''
                  }`
                "
              >
                <AttemptsIcon class="icon" />
              </div>
              <div
                @click="setTab(tabs.solutions)"
                :class="
                  `button solutions-tab ${
                    activeTab === tabs.solutions ? 'active' : ''
                  }`
                "
              >
                <SolutionsIcon class="icon" />
              </div>
            </div>
            <div class="exercise-modal__left">
              <div class="description-view" v-if="activeTab === tabs.exercise">
                <div class="description-wrapper">
                  <accordion title="DESCRIPTION" @open="updatePrism()">
                    <template v-slot:body>
                      <div
                        class="description markdown-styles"
                        ref="description"
                        v-html="exercise.description_html"
                      ></div>
                    </template>
                  </accordion>
                  <accordion title="TESTS CASES">
                    <template v-slot:header>
                      <span
                        class="exercise-btn transparent pointer run-all-tests"
                        @click.stop="runAllTests()"
                      >
                        RUN ALL
                      </span>
                    </template>
                    <template v-slot:body>
                      <Tests :exercise="exercise" :code="currentCode" />
                    </template>
                  </accordion>
                </div>
              </div>

              <div class="files-view" v-if="activeTab === tabs.files">
                <div class="files-list">
                  <Files
                    :content="exercise"
                    title="Files associated with this lesson"
                    :tooltip="true"
                  >
                    <template v-slot:no-files>
                      <p>No files in this exercise.</p>
                    </template>
                  </Files>
                </div>
              </div>

              <div class="attempts-view" v-if="activeTab === tabs.attempts">
                <div class="attempts-list">
                  <Attempts :exercise="exercise">
                    <template v-slot:no-attempts>
                      <p>No attempts yet.</p>
                    </template>
                  </Attempts>
                </div>
              </div>

              <div class="solutions-view" v-if="activeTab === tabs.solutions">
                <div class="solution-list">
                  <Solutions :exercise="exercise">
                    <template v-slot:no-solutions>
                      <p>No solutions in this exercise.</p>
                    </template>
                  </Solutions>
                </div>
              </div>
            </div>
            <div class="exercise-modal__right">
              <div class="code-header">
                <span>CODE</span>
                <div
                  class="monaco-options"
                  :class="`${monacoMenuOpen ? 'open' : 'close'}`"
                >
                  <div class="dot-menu">
                    <input
                      id="dot-menu"
                      type="checkbox"
                      class="dot-menu__checkbox"
                    />
                    <label
                      @click.stop="monacoMenuOpen = !monacoMenuOpen"
                      for="dot-menu"
                      class="dot-menu__label"
                      ><span>Menu</span></label
                    >
                  </div>
                  <transition name="fade">
                    <div v-if="monacoMenuOpen" class="monaco-options__submenu">
                      <h5>Preferences</h5>
                      <div class="monaco-options__submenu-reset">
                        <ResetIcon />
                        <span>Reset code</span>
                        <div
                          class="pointer monaco-options__submenu-reset__btn"
                          @click.stop="resetCode()"
                        >
                          <ReloadIcon />
                        </div>
                      </div>
                      <div class="theme">
                        <SunIcon />
                        <span>Light mode</span>
                        <label class="btn btn__switch" for="theme">
                          <input
                            type="checkbox"
                            id="theme"
                            v-model="lightTheme"
                          />
                          <div class="slider round"></div>
                        </label>
                      </div>
                      <div class="flavor">
                        <CubeIcon class="cube-icon" />
                        <span>Flavor:</span>
                        <select
                          v-model="currentFlavor"
                          class="monaco-options__flavor-selector"
                        >
                          <option value="ds-3.8" selected>Python 3.8</option>
                          <option value="ds-3.7">Python 3.7</option>
                        </select>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
              <div class="only-mobile">
                Continue on desktop for an enhanced experience.
              </div>
              <div class="code-editor">
                <MonacoEditor
                  class="monaco-editor"
                  :theme="editorTheme"
                  :language="exercise.language"
                  v-model="currentCode"
                ></MonacoEditor>
                <div class="action-buttons">
                  <div class="lds-ring" v-if="running">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  <button
                    class="exercise-modal__button exercise-modal__button--dark run-code"
                    @click="runCode()"
                    :disabled="running"
                  >
                    RUN CODE
                  </button>
                  <button
                    class="exercise-modal__button exercise-modal__button--orange submit-code"
                    @click="submitCode()"
                    :disabled="running"
                  >
                    SUBMIT SOLUTION
                  </button>
                </div>
              </div>
              <div class="std-console">
                <accordion title="OUTPUT" :transparent="true">
                  <template v-slot:header>
                    <SuccessIcon v-if="currentOutput && lastRun.success" />
                    <FailIcon v-if="currentOutput && !lastRun.success" />
                  </template>
                  <template v-slot:body>
                    <div class="std-output" v-html="currentOutput"></div>
                  </template>
                </accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonacoEditor from 'monaco-editor-vue'
import { highlightAllUnder } from 'prismjs'
import { mapState } from 'vuex'
import ExerciseIcon from '@/assets/icons/course/exercise/main-exercise-icon.svg'
import FilesIcon from '@/assets/icons/course/exercise/files-icon.svg'
import AttemptsIcon from '@/assets/icons/course/exercise/attempts-icon.svg'
import SolutionsIcon from '@/assets/icons/course/exercise/solutions-icon.svg'
import ArrowLeft from '@/assets/icons/course/exercise/arrow-left.svg'
import SuccessIcon from '@/assets/icons/course/exercise/success-icon.svg'
import FailIcon from '@/assets/icons/course/exercise/fail-icon.svg'
import SunIcon from '@/assets/icons/course/exercise/sun-icon.svg'
import ResetIcon from '@/assets/icons/course/exercise/reset-icon.svg'
import ReloadIcon from '@/assets/icons/course/exercise/reload-icon.svg'
import CubeIcon from '@/assets/icons/course/exercise/cube_icon.svg'

import TitleBar from './TitleBar.vue'
import Accordion from './Accordion.vue'
import Tests from './Tests.vue'
import Attempts from './Attempts.vue'
import Solutions from './Solutions.vue'
import Files from './Files.vue'

import '@/assets/css/prism-theme.css'
import 'prismjs/components/prism-python'

export default {
  name: 'ExerciseModal',
  components: {
    TitleBar,
    MonacoEditor,
    Accordion,
    Tests,
    Attempts,
    Solutions,
    Files,
    ExerciseIcon: { ...ExerciseIcon, name: 'ExerciseIcon' },
    FilesIcon: { ...FilesIcon, name: 'FilesIcon' },
    AttemptsIcon: { ...AttemptsIcon, name: 'AttemptsIcon' },
    SolutionsIcon: { ...SolutionsIcon, name: 'SolutionsIcon' },
    ArrowLeft: { ...ArrowLeft, name: 'ArrowLeft' },
    SuccessIcon: { ...SuccessIcon, name: 'SuccessIcon' },
    FailIcon: { ...FailIcon, name: 'FailIcon' },
    SunIcon: { ...SunIcon, name: 'SunIcon' },
    ResetIcon: { ...ResetIcon, name: 'ResetIcon' },
    ReloadIcon: { ...ReloadIcon, name: 'ReloadIcon' },
    CubeIcon: { ...CubeIcon, name: 'CubeIcon' },
  },
  props: {
    exercise: {
      type: Object,
      required: true,
    },
    exercisesList: {
      type: Array,
      required: true,
    },
    canSendIssues: {
      type: Boolean,
      required: false,
      default: () => {
        return true
      },
    },
    context: {
      type: String,
      required: false,
      default: () => {
        return 'course'
      },
    },
  },
  data: () => {
    return {
      lightTheme: false,
      options: {
        // Monaco Editor Options
      },
      tabs: {
        exercise: 'exercise',
        files: 'files',
        attempts: 'attempts',
        solutions: 'solutions',
      },
      activeTab: null,
      running: false,
      lastRun: {},
      monacoMenuOpen: false,
    }
  },
  computed: {
    ...mapState({
      currentCourse: state => state.courses.currentCourse,
      learningPath: state => state.learningPaths.currentLearningPath,
    }),
    currentCode: {
      get() {
        return this.$store.state.exercises.studentCodes[this.exercise.id]
      },
      set(value) {
        this.$store.commit('exercises/UPDATE_CURRENT_CODE', {
          id: this.exercise.id,
          code: value,
        })
      },
    },
    currentFlavor: {
      get() {
        return this.$store.state.exercises.currentFlavor
      },
      set(value) {
        this.$store.commit('exercises/CHANGE_CURRENT_FLAVOR', value)
      },
    },
    currentOutput: {
      get() {
        return this.$store.state.exercises.currentOutput
      },
    },
    currentExerciseIndex() {
      return this.exercisesList.indexOf(this.exercise) + 1
    },
    editorTheme() {
      return this.lightTheme ? 'vs-light' : 'vs-dark'
    },
    contextData() {
      if (this.context === 'course') {
        return {
          course_id: this.currentCourse.id,
          course_name: this.currentCourse.name,
        }
      }
      return {
        learning_path_id: this.learningPath.id,
        learning_path_name: this.learningPath.name,
      }
    },
  },
  methods: {
    onChange(value) {
      this.currentCode = value
    },
    runAllTests() {
      this.$store.dispatch('exercises/runAllExerciseTests')
    },
    async runCode() {
      this.running = true
      this.lastRun = await this.$store.dispatch('exercises/runExerciseCode')
      this.running = false
    },
    async submitCode() {
      this.running = true
      this.lastRun = await this.$store.dispatch('exercises/runExerciseAttempt')
      this.running = false
      this.sendMixpanelEvent(this.eventsConstants.CONTENT_EXERCISE_SUBMITTED, {
        ...this.contextData,
        exercise_id: this.exercise.id,
        exercise_name: this.exercise.name,
        ...this.exercise,
      })
      if (this.lastRun.success) {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_EXERCISE_FINISHED, {
          ...this.contextData,
          exercise_id: this.exercise.id,
          exercise_name: this.exercise.name,
          ...this.exercise,
        })
      }
    },
    setTab(tab) {
      this.activeTab = tab
    },
    updatePrism() {
      this.$nextTick().then(() => {
        if (this.$refs.description) {
          highlightAllUnder(this.$refs.description)
          if (window.MathJax) {
            window.MathJax.Hub.Config({
              tex2jax: {
                inlineMath: [['$', '$']],
                displayMath: [['$$', '$$']],
                processEscapes: true,
                processEnvironments: true,
              },
              // Center justify equations in code and markdown cells. Elsewhere
              // we use CSS to left justify single line equations in code cells.
              displayAlign: 'center',
              'HTML-CSS': {
                styles: { '.MathJax_Display': { margin: 0 } },
                linebreaks: { automatic: true },
              },
            })
            window.MathJax.Hub.Queue([
              'Typeset',
              window.MathJax.Hub,
              this.$refs.description,
            ])
          }
        }
      })
    },
    changeTheme(e) {
      if (this.editorTheme === 'vs-dark') {
        this.editorTheme = 'vs-light'
      } else {
        this.editorTheme = 'vs-dark'
      }
    },
    resetCode() {
      this.$store.dispatch('exercises/resetExerciseCode', this.exercise)
    },
    startIssue() {
      this.$store.dispatch('courses/saveContentIssue', {
        content_type: 'exercise',
        content_subtype: '',
        content_name: this.exercise.name,
        content_id: this.exercise.id,
        extra_args: {
          student_code: this.currentCode,
        },
      })
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.activeTab = this.tabs.exercise
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  mounted() {
    this.updatePrism()
  },
  watch: {
    exercise() {
      this.updatePrism()
    },
    activeTab(newValue) {
      if (newValue === this.tabs.exercise) {
        this.updatePrism()
      }
    },
  },
}
</script>

<style lang="scss">
.exercise-modal {
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--dark-gray);
    z-index: 205;
    overflow-y: auto;
    color: var(--white);
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 206;
    position: relative;
  }
  &__content-wrapper {
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    max-width: 2800px;
    height: 100%;
  }
  &__header-arrow {
    cursor: pointer;
    &--rotate {
      transform: rotate(180deg);
    }
  }
  code {
    line-height: 2;
  }
  .only-mobile {
    display: none;
  }
  &__header-nav {
    &__numbers {
      background: #3f3f3f;
      padding: 8px 15px;
      margin: 10px;
      border-radius: 15px;
    }
  }
  &__button {
    border-radius: 15px;
    padding: 5px 10px;
    font-size: 11px;
    color: white;
    cursor: pointer;
    &--transparent {
      border: 1px solid var(--orange);
      background: transparent;
    }
    &--dark {
      border: 1px solid var(--orange);
      background: black;
    }
    &--orange {
      border: 1px solid var(--orange);
      background: var(--orange);
    }
  }
  &__left {
    .codehilite {
      font-size: 0.95em;
      background: black;
      overflow: auto;
    }
    table {
      background: #3d3d3d;
      border-radius: 0.25em;
      border-collapse: collapse;
      margin: 1em;
      margin-bottom: 20px;
      a {
        color: #75beff;
      }
    }
    th {
      border-bottom: 1px solid #364043;
      color: var(--orange);
      font-size: 0.85em;
      font-weight: 600;
      padding: 0.5em 1em;
      text-align: left;
    }
    td {
      color: #fff;
      font-weight: 400;
      padding: 0.65em 1em;
    }
    tbody tr {
      transition: all 0.25s ease;
    }
    tbody tr:hover {
      background: #333333;
    }
  }
  &__right {
    .code-header {
      padding: 20px 25px 20px 46px;
      font-size: 14px;
      display: flex;
      align-items: center;
      position: relative;
    }
    .monaco-options {
      margin-left: auto;
      &__submenu {
        position: absolute;
        top: 70px;
        right: 0px;
        z-index: 99;
        background: black;
        padding: 30px 30px;
        min-width: 250px;
        line-height: 40px;
        span {
          margin-left: 8px;
        }
        h5 {
          margin: 0;
          margin-bottom: 10px;
        }
        .theme {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          .btn__switch {
            margin-left: auto;
          }
        }
        .flavor {
          display: flex;
          align-items: center;
          .cube-icon {
            width: 20px;
            height: 20px;
          }
          .flavor-data {
            margin-left: auto;
          }
        }
        &-reset {
          display: flex;
          align-items: center;
          border-bottom: 1px solid #2c2c2c;
          padding-bottom: 5px;
          margin-bottom: 5px;
          &__btn {
            background: #3866ff;
            width: 40px;
            height: 20px;
            border-radius: 34px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: auto;
          }
        }
      }
      &__flavor-selector {
        padding: 5px 10px;
        background: #f4f4f4;
        border: 1px solid transparent;
        margin-left: auto;
        &:focus {
          border: 1px solid var(--orange);
        }
      }
    }
    .code-editor {
      flex: 1;
      overflow: hidden;
      position: relative;

      .action-buttons {
        position: absolute;
        bottom: 22px;
        right: 25px;

        .run-code {
          margin-right: 10px;
          margin-left: 10px;
        }
      }

      .monaco-editor {
        padding-top: 10px;
      }
    }
    .std-console {
      .std-output {
        color: var(--white);
        font-family: var(--font-roboto);
        font-size: 12px;

        .success {
          color: var(--green);
        }
        .error,
        .errors {
          color: var(--red);
        }
        .info {
          color: var(--yellow);
        }
        .output {
          color: white;
        }
      }
    }
  }

  @include tablet {
    &__left-navbar {
      flex: 0 0 100px;
    }

    &__body {
      display: flex;
      width: 100%;
      height: calc(100% - 100px);
      overflow: hidden;
    }
    &__left {
      width: 45%;
      max-height: calc(100vh - 100px);
      .files-list,
      .attempts-list,
      .solutions-view {
        overflow: auto;
        max-height: calc(100vh - 110px);
      }
    }
    &__right {
      flex: 1;
      width: 45%;
      padding: 0;
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 100px);

      .code-editor {
        min-height: calc(60% - 160px);
      }
      .std-console {
        .accordion__body {
          overflow: auto;
          max-height: calc(50vh - 160px);
        }
      }
    }
  }
  @media (max-width: $mobile) {
    &__left-navbar,
    .code-editor,
    .std-console {
      display: none;
    }
    pre {
      white-space: pre-wrap;
      word-wrap: break-word;
      text-align: justify;
    }
    .test-wrapper .prism-code,
    .test-run-wrapper {
      max-width: calc(100vw - 25px);
    }
    .accordion__header,
    .accordion__body {
      padding: 20px 10px;
      min-width: 100vw;
      ul {
        padding-left: 15px;
      }
    }
    .only-mobile {
      min-height: 80px;
      display: flex;
      align-items: center;
      background: #ffffff42;
      text-align: center;
      padding: 50px;
    }
    .monaco-options {
      display: none;
    }
    .title-bar__title-wrapper {
      width: 15%;
    }
  }
  &__left-navbar {
    border-right: 1px solid #424040;

    .button {
      padding: 30px 20px;
      text-align: center;
      cursor: pointer;
      .icon {
        fill: #727276;
      }
      &.active {
        border-left: 4px solid var(--orange);
        background: black;
        .icon {
          fill: white;
        }
      }
      &:hover {
        .icon {
          fill: white;
        }
      }
    }
    .files-tab {
      display: flex;
      justify-content: center;
      .file-icon {
        position: relative;
        width: 50px;
        .files-number {
          position: absolute;
          top: -7px;
          right: 1px;
          background: var(--orange);
          border-radius: 15px;
          font-size: 11px;
          padding: 0px 6px;
        }
      }
    }
  }

  .description-view {
    .description-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      flex: 1;
      overflow: auto;
      a {
        color: var(--blue);
      }
      code:not([class*='language-']) {
        color: white;
        background: #1e1e1e;
        padding: 5px 7px;
        border-radius: 10px;
        font-size: 0.9em;
        overflow: auto;
      }
      .prism-code,
      .codehilite {
        code {
          background: black;
        }
      }
      .accordion {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
        &.opened {
          height: 100%;
        }
        &__body {
          flex: 1;
        }
      }
    }

    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .lds-ring {
    display: inline-block;
    position: relative;
    width: 15px;
    height: 15px;
    padding-top: 2px;
    div {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 15px;
      height: 15px;
      border: 2px solid rgb(129, 129, 129);
      border-radius: 50%;
      animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: rgb(129, 129, 129) transparent transparent transparent;
    }
    div:nth-child(1) {
      animation-delay: -0.45s;
    }
    div:nth-child(2) {
      animation-delay: -0.3s;
    }
    div:nth-child(3) {
      animation-delay: -0.15s;
    }
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
